import React, { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { Elements, PaymentElement, useElements, useStripe } from "@stripe/react-stripe-js";
import { stripePromise } from "../lib/stripe";
import capi from "../lib/consumerApi";
import { useConsumer } from "./ConsumerAuthContext";
import { PaymentSheet } from "../components/PaymentSheet";
import { ShieldCheck, Lock, Zap, ArrowUpRight, CreditCard, Check, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { planFeatureList } from "../pages/consumer/ConsumerPlans";

const Ctx = createContext(null);
export const useBilling = () => useContext(Ctx);

// Stable references so re-renders never re-initialize Stripe Elements (which would
// collapse/remount the card panel). Do NOT inline these into JSX.
const PE_OPTIONS = { layout: "tabs" };
export const STRIPE_APPEARANCE = { theme: "stripe", variables: { colorPrimary: "#22d3ee" } };

function PayForm({ label, onConfirmed, onClose, backLabel = "Cancel" }) {
  const stripe = useStripe();
  const elements = useElements();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setBusy(true); setErr("");
    const { error } = await stripe.confirmPayment({ elements, confirmParams: { return_url: window.location.href }, redirect: "if_required" });
    if (error) { setErr(error.message || "Payment failed"); setBusy(false); return; }
    await onConfirmed();
    setBusy(false);
  };
  return (
    <form onSubmit={submit} className="space-y-4" data-testid="payment-form">
      <PaymentElement options={PE_OPTIONS} />
      {err && <p className="text-[12.5px] text-red-500" data-testid="payment-error">{err}</p>}
      <div className="flex gap-2">
        <button type="button" onClick={onClose} className="h-11 px-4 rounded-xl text-[13px] text-neutral-600 border border-neutral-300 hover:bg-neutral-50">{backLabel}</button>
        <button type="submit" disabled={!stripe || busy} data-testid="pay-submit" className="flex-1 h-11 rounded-xl text-[13.5px] font-semibold text-[#05070d] flex items-center justify-center gap-2 disabled:opacity-60" style={{ background: "linear-gradient(90deg,#22d3ee,#8b5cf6)" }}>
          <Lock size={15} /> {busy ? "Processing…" : label}
        </button>
      </div>
      <p className="text-[10.5px] text-neutral-400 flex items-center gap-1.5"><ShieldCheck size={12} /> Payments are securely processed by Stripe.</p>
    </form>
  );
}

export function ConsumerBillingProvider({ children }) {
  const { user, refresh } = useConsumer();
  const [me, setMe] = useState(null);
  const [sheet, setSheet] = useState(null);   // one-off payments: {clientSecret, title, label, confirm}
  const [upg, setUpg] = useState(null);        // upgrade flow: {step:"quota"|"plans"|"pay", mode, price, clientSecret, label, confirm, back}
  const [aiUpg, setAiUpg] = useState(null);    // manual→AI agreement upgrade: {step:"intro"|"pay"|"done", price, clientSecret, paymentIntentId, contractId}
  const [planInterval, setPlanInterval] = useState("month");
  const [plans, setPlans] = useState(null);
  const resolver = useRef(null);

  const refreshMe = useCallback(async () => {
    try { const { data } = await capi.get("/consumer/billing/me"); setMe(data); return data; } catch { return null; }
  }, []);
  useEffect(() => { if (user) { refreshMe(); capi.get("/consumer/billing/plans").then((r) => setPlans(r.data.plans)).catch(() => {}); } }, [user, refreshMe]);

  const closeSheet = () => { setSheet(null); if (resolver.current) { resolver.current(false); resolver.current = null; } };

  const openSubscribe = async (plan, interval) => {
    if (plan === "starter") {
      await capi.post("/consumer/billing/subscribe", { plan, interval });
      await refresh(); await refreshMe();
      toast.success("You're on the Starter plan");
      return true;
    }
    const { data } = await capi.post("/consumer/billing/subscribe", { plan, interval });
    return new Promise((resolve) => {
      resolver.current = resolve;
      setSheet({
        clientSecret: data.clientSecret,
        title: `Subscribe — ${plan === "pro" ? "Pro" : "Business"} (${interval === "year" ? "Annual" : "Monthly"})`,
        label: "Start subscription",
        confirm: async () => {
          const { data: c } = await capi.post("/consumer/billing/confirm", { subscription_id: data.subscriptionId });
          await refresh(); await refreshMe();
          setSheet(null);
          if (c.status === "active") { toast.success("Subscription active"); if (resolver.current) { resolver.current(true); resolver.current = null; } }
          else { toast.error("Payment is still processing"); if (resolver.current) { resolver.current(false); resolver.current = null; } }
        },
      });
    });
  };

  const openAlacarte = async (item) => {
    const { data } = await capi.post("/consumer/billing/alacarte", { item });
    return new Promise((resolve) => {
      resolver.current = resolve;
      setSheet({
        clientSecret: data.clientSecret,
        title: item === "ai" ? "Pay for 1 AI creation — $25" : "Pay for 1 manual creation — $10",
        label: `Pay $${item === "ai" ? 25 : 10}`,
        confirm: async () => {
          const { data: c } = await capi.post("/consumer/billing/confirm", { payment_intent_id: data.paymentIntentId });
          await refresh(); await refreshMe();
          setSheet(null);
          if (c.status === "succeeded") { toast.success("Payment complete — you have 1 credit"); if (resolver.current) { resolver.current(true); resolver.current = null; } }
          else { toast.error("Payment failed"); if (resolver.current) { resolver.current(false); resolver.current = null; } }
        },
      });
    });
  };

  // ---- Unified upgrade flow (single stepped popup: quota -> plans -> stripe) ----
  const requireCreation = async (mode) => {
    const { data } = await capi.get(`/consumer/billing/can-create`, { params: { mode } });
    if (data.allowed) return true;
    return new Promise((resolve) => { resolver.current = resolve; setUpg({ step: "gate", mode, price: data.price }); });
  };

  const upgResolve = (v) => { setUpg(null); if (resolver.current) { resolver.current(v); resolver.current = null; } };
  const upgBack = () => setUpg((u) => ({ ...u, step: "gate", clientSecret: null }));

  const upgChoosePlan = async (plan, interval) => {
    try {
      const { data } = await capi.post("/consumer/billing/subscribe", { plan, interval });
      setUpg((u) => ({
        ...u, step: "pay", back: "plans", clientSecret: data.clientSecret, label: "Start subscription",
        confirm: async () => {
          const { data: c } = await capi.post("/consumer/billing/confirm", { subscription_id: data.subscriptionId });
          await refresh(); await refreshMe();
          if (c.status === "active") { toast.success("Subscription active"); upgResolve(true); }
          else { toast.error("Payment is still processing"); }
        },
      }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start plan"); }
  };

  const upgPayOne = async () => {
    try {
      const mode = upg.mode;
      const { data } = await capi.post("/consumer/billing/alacarte", { item: mode });
      setUpg((u) => ({
        ...u, step: "pay", back: "quota", clientSecret: data.clientSecret, label: `Pay $${mode === "ai" ? 25 : 10}`,
        confirm: async () => {
          const { data: c } = await capi.post("/consumer/billing/confirm", { payment_intent_id: data.paymentIntentId });
          await refresh(); await refreshMe();
          if (c.status === "succeeded") { toast.success("Payment complete — you have 1 credit"); upgResolve(true); }
          else { toast.error("Payment failed"); }
        },
      }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start payment"); }
  };

  // ---- Manual → AI agreement upgrade (same bottom sheet as Stripe payments) ----
  const upgradeAgreementAI = async (contractId) => {
    let price = 2000, freeCredit = false;
    try { const { data } = await capi.get("/consumer/upgrade-ai/status"); if (data.price_cents) price = data.price_cents; freeCredit = !!data.free_credit; } catch { /* use defaults */ }
    return new Promise((resolve) => { resolver.current = resolve; setAiUpg({ step: "intro", price, freeCredit, contractId }); });
  };
  const aiUpgClose = () => { setAiUpg(null); if (resolver.current) { resolver.current(false); resolver.current = null; } };
  const aiUpgStart = async () => {
    try {
      const { data } = await capi.post("/consumer/contracts/upgrade-ai/checkout", { contract_id: aiUpg.contractId });
      setAiUpg((u) => ({ ...u, step: "pay", clientSecret: data.clientSecret, paymentIntentId: data.paymentIntentId, price: data.price_cents }));
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start the upgrade"); }
  };
  const aiUpgUseCredit = async () => {
    try {
      const { data: c } = await capi.post("/consumer/contracts/upgrade-ai/use-credit", { contract_id: aiUpg.contractId });
      if (c.status === "succeeded") {
        await refresh(); await refreshMe();
        setAiUpg((u) => ({ ...u, step: "done" }));
        setTimeout(() => { setAiUpg(null); if (resolver.current) { resolver.current(c.contract || true); resolver.current = null; } }, 1600);
      }
    } catch (e) { toast.error(e.response?.data?.detail || "Could not use your credit"); }
  };
  const aiUpgConfirm = async () => {
    const { data: c } = await capi.post("/consumer/contracts/upgrade-ai/confirm", { contract_id: aiUpg.contractId, payment_intent_id: aiUpg.paymentIntentId });
    if (c.status === "succeeded") {
      await refresh(); await refreshMe();
      setAiUpg((u) => ({ ...u, step: "done" }));
      setTimeout(() => { setAiUpg(null); if (resolver.current) { resolver.current(c.contract || true); resolver.current = null; } }, 1600);
    } else { toast.error("Payment failed"); }
  };

  const openSeat = async () => {
    const { data } = await capi.post("/consumer/workspace/seat");
    return new Promise((resolve) => {
      resolver.current = resolve;
      setSheet({
        clientSecret: data.clientSecret, title: "Add a team seat — $15/mo", label: "Add seat ($15/mo)",
        confirm: async () => {
          await capi.post("/consumer/billing/confirm", { subscription_id: data.subscriptionId });
          await refresh(); await refreshMe(); setSheet(null);
          toast.success("Seat added — +1 AI creation / month");
          if (resolver.current) { resolver.current(true); resolver.current = null; }
        },
      });
    });
  };
  const cancelPlan = async () => {
    await capi.post("/consumer/billing/cancel"); await refresh(); await refreshMe();
    toast.success("Subscription canceled — you're on Starter");
  };

  const openReport = async (noticeId) => {
    const { data } = await capi.post("/consumer/verify-report/checkout", { notice_id: noticeId });
    if (data.comped) { toast.success("Report unlocked with a granted credit"); return data.report; }
    if (data.paid) {
      const { data: r } = await capi.post("/consumer/verify-report/confirm", { notice_id: noticeId });
      return r.report;
    }
    const price = (data.price_cents / 100).toFixed(2);
    return new Promise((resolve) => {
      resolver.current = resolve;
      setSheet({
        clientSecret: data.clientSecret,
        title: `Unlock verification report — $${price}`,
        label: `Pay $${price} & reveal`,
        confirm: async () => {
          const { data: c } = await capi.post("/consumer/verify-report/confirm", { notice_id: noticeId, payment_intent_id: data.paymentIntentId });
          setSheet(null);
          if (c.status === "succeeded") { toast.success("Report unlocked"); if (resolver.current) { resolver.current(c.report); resolver.current = null; } }
          else { toast.error("Payment failed"); if (resolver.current) { resolver.current(null); resolver.current = null; } }
        },
      });
    });
  };

  const payFor = async ({ checkout, confirm, extra = {}, title, label }) => {
    const { data } = await capi.post(checkout, extra);
    if (!data.clientSecret) return data;
    const price = (data.price_cents / 100).toFixed(2);
    return new Promise((resolve) => {
      resolver.current = resolve;
      setSheet({
        clientSecret: data.clientSecret,
        title: title || `Pay $${price}`,
        label: label || `Pay $${price}`,
        confirm: async () => {
          const { data: c } = await capi.post(confirm || checkout, { ...extra, payment_intent_id: data.paymentIntentId });
          setSheet(null);
          const ok = c.status === "succeeded" || c.status === "requested";
          if (ok) toast.success("Payment complete"); else toast.error("Payment failed");
          if (resolver.current) { resolver.current(ok ? c : null); resolver.current = null; }
        },
      });
    });
  };

  // Locked = a free Starter user who has exhausted their lifetime allowance (2 manual + 1 AI)
  // with no à-la-carte credits and no paid plan. When locked, every builder action prompts upgrade.
  const locked = !!(me && me.plan === "starter"
    && !me.manual?.unlimited && (me.manual?.remaining || 0) <= 0 && (me.manual?.credits || 0) <= 0
    && (me.ai?.remaining || 0) <= 0 && (me.ai?.credits || 0) <= 0);
  const requireActive = () => { if (!locked) return true; setUpg({ step: "gate" }); return false; };

  const upgSubtitle = upg?.step === "pay"
    ? "Complete your purchase"
    : upg?.mode ? `You've used your ${upg.mode === "ai" ? "AI" : "manual"} creations` : "Unlock Lexarra to continue";

  return (
    <Ctx.Provider value={{ me, plans, refreshMe, openSubscribe, openAlacarte, requireCreation, requireActive, locked, openSeat, cancelPlan, openReport, payFor, upgradeAgreementAI }}>
      {children}

      {sheet && (
        <PaymentSheet subtitle={sheet.title} onClose={closeSheet}>
          {sheet.clientSecret && (
            <Elements key={sheet.clientSecret} stripe={stripePromise} options={{ clientSecret: sheet.clientSecret, appearance: STRIPE_APPEARANCE }}>
              <PayForm label={sheet.label} onConfirmed={sheet.confirm} onClose={closeSheet} />
            </Elements>
          )}
        </PaymentSheet>
      )}

      {upg && (
        <PaymentSheet subtitle={upg.step === "pay" ? "Complete your purchase" : null} onClose={() => upgResolve(false)}>
          {upg.step === "gate" && (
            <div data-testid="plan-gate-sheet" className="mx-auto w-full max-w-2xl">
              <div className="text-center mb-5">
                <h4 className="text-[16px] font-semibold text-neutral-900">{upg.mode ? `You've used your ${upg.mode === "ai" ? "AI" : "manual"} creations` : "Unlock Lexarra to continue"}</h4>
                <p className="text-[13px] text-neutral-600 mt-1 max-w-md mx-auto">{upg.mode ? "You're out of creations for now. Upgrade to increase your limit or pay for one additional creation." : "Choose a plan to keep drafting, negotiating and issuing agreements."}</p>
              </div>
              <div className="flex justify-center mb-5">
                <div className="inline-flex rounded-full border border-neutral-200 p-1 text-[12.5px]">
                  {["month", "year"].map((iv) => (<button key={iv} onClick={() => setPlanInterval(iv)} data-testid={`plan-gate-interval-${iv}`} className={`px-3.5 py-1.5 rounded-full transition-colors ${planInterval === iv ? "bg-neutral-900 text-white font-semibold" : "text-neutral-500"}`}>{iv === "month" ? "Monthly" : "Annual"}</button>))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                {plans && ["pro", "business"].map((k) => ({ p: k, name: plans[k].name, price: planInterval === "year" ? `$${plans[k].price_year}/yr` : `$${plans[k].price_month}/mo`, sub: plans[k].term, feats: planFeatureList(plans[k]).slice(0, 4), featured: k === "pro" })).map((pl) => (
                  <div key={pl.p} data-testid={`plan-gate-${pl.p}`} className={`rounded-2xl border p-4 flex flex-col ${pl.featured ? "border-cyan-500 bg-cyan-50" : "border-neutral-200 bg-white"}`}>
                    {pl.featured && <div className="text-[10px] uppercase tracking-wider text-cyan-700 font-semibold mb-1">Most popular</div>}
                    <div className="text-[15px] font-semibold text-neutral-900">{pl.name}</div>
                    <div className="text-2xl font-semibold mt-1 text-neutral-900">{pl.price}</div>
                    <div className="text-[12px] text-neutral-500 mb-3">{pl.sub}</div>
                    <ul className="space-y-1.5 mb-4 flex-1">{pl.feats.map((f, i) => (<li key={i} className="flex items-center gap-1.5 text-[12.5px] text-neutral-700"><Check size={13} className="text-cyan-600" /> {f}</li>))}</ul>
                    <button onClick={() => upgChoosePlan(pl.p, planInterval)} data-testid={`plan-gate-choose-${pl.p}`} className="h-10 rounded-xl text-[13px] font-semibold text-[#05070d] flex items-center justify-center gap-1" style={{ background: "linear-gradient(90deg,#22d3ee,#8b5cf6)" }}>Choose {pl.name} <ArrowUpRight size={15} /></button>
                  </div>
                ))}
              </div>
              {upg.mode && (
                <button onClick={upgPayOne} data-testid="quota-pay" className="mt-3 w-full rounded-2xl border border-neutral-200 bg-neutral-50 hover:bg-neutral-100 p-4 flex items-center justify-between transition-colors text-left">
                  <div>
                    <div className="text-[14px] font-semibold text-neutral-900">Just this once</div>
                    <div className="text-[12px] text-neutral-500">Pay for a single {upg.mode === "ai" ? "AI" : "manual"} creation — no subscription</div>
                  </div>
                  <span className="text-[15px] font-semibold text-neutral-900 flex items-center gap-1.5"><CreditCard size={16} /> ${upg.price}</span>
                </button>
              )}
              <button onClick={() => upgResolve(false)} data-testid="plan-gate-dismiss" className="w-full h-9 mt-3 text-[12.5px] text-neutral-400 hover:text-neutral-700">Not now</button>
            </div>
          )}

          {upg.step === "pay" && upg.clientSecret && (
            <div className="mx-auto w-full max-w-lg">
              <Elements key={upg.clientSecret} stripe={stripePromise} options={{ clientSecret: upg.clientSecret, appearance: STRIPE_APPEARANCE }}>
                <PayForm label={upg.label} onConfirmed={upg.confirm} onClose={upgBack} backLabel="Back" />
              </Elements>
            </div>
          )}
        </PaymentSheet>
      )}

      {aiUpg && (
        <PaymentSheet subtitle={aiUpg.step === "pay" ? "Complete your purchase" : null} onClose={aiUpgClose}>
          {aiUpg.step === "intro" && (
            <div data-testid="ai-upgrade-intro" className="mx-auto w-full max-w-md text-center py-2">
              <div className="w-12 h-12 rounded-xl mx-auto mb-4 flex items-center justify-center" style={{ background: "linear-gradient(135deg,#22d3ee,#8b5cf6)" }}><Sparkles size={22} className="text-[#05070d]" /></div>
              <h4 className="text-[17px] font-semibold text-neutral-900">Let AI help you draft this agreement</h4>
              <p className="text-[13px] text-neutral-600 mt-2 max-w-sm mx-auto">Upgrade this manual draft to our AI assistant. It reads everything you've built, summarizes it, and helps you refine any clause in plain language.</p>
              {aiUpg.freeCredit ? (
                <>
                  <div className="text-[15px] font-semibold mt-5 text-emerald-600" data-testid="ai-upgrade-free-credit">You have a free upgrade credit</div>
                  <button onClick={aiUpgUseCredit} data-testid="ai-upgrade-use-credit" className="mt-5 w-full h-11 rounded-xl text-[13.5px] font-semibold text-[#05070d] flex items-center justify-center gap-1.5" style={{ background: "linear-gradient(90deg,#22d3ee,#8b5cf6)" }}>Use Free Upgrade Credit <ArrowUpRight size={16} /></button>
                </>
              ) : (
                <>
                  <div className="text-3xl font-semibold mt-5 text-neutral-900">${(aiUpg.price / 100).toFixed(2)}</div>
                  <div className="text-[12px] text-neutral-500">one-time upgrade</div>
                  <button onClick={aiUpgStart} data-testid="ai-upgrade-start" className="mt-5 w-full h-11 rounded-xl text-[13.5px] font-semibold text-[#05070d] flex items-center justify-center gap-1.5" style={{ background: "linear-gradient(90deg,#22d3ee,#8b5cf6)" }}>Upgrade <ArrowUpRight size={16} /></button>
                </>
              )}
              <button onClick={aiUpgClose} data-testid="ai-upgrade-dismiss" className="w-full h-9 mt-2 text-[12.5px] text-neutral-400 hover:text-neutral-700">Not now</button>
            </div>
          )}
          {aiUpg.step === "pay" && aiUpg.clientSecret && (
            <div className="mx-auto w-full max-w-lg">
              <Elements key={aiUpg.clientSecret} stripe={stripePromise} options={{ clientSecret: aiUpg.clientSecret, appearance: STRIPE_APPEARANCE }}>
                <PayForm label={`Pay $${(aiUpg.price / 100).toFixed(2)}`} onConfirmed={aiUpgConfirm} onClose={() => setAiUpg((u) => ({ ...u, step: "intro", clientSecret: null }))} backLabel="Back" />
              </Elements>
            </div>
          )}
          {aiUpg.step === "done" && (
            <div data-testid="ai-upgrade-done" className="mx-auto w-full max-w-md py-12 flex flex-col items-center">
              <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mb-4"><Check size={34} className="text-emerald-600" /></div>
              <h4 className="text-[16px] font-semibold text-neutral-900">Payment complete</h4>
              <p className="text-[13px] text-neutral-600 mt-1">Switching to AI mode…</p>
            </div>
          )}
        </PaymentSheet>
      )}
    </Ctx.Provider>
  );
}
